import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { PackChatRefreshError } from "@/payroll/pack-chat/pack-chat-refresh-error";
import { PackChatState } from "@/payroll/pack-chat/pack-chat-state";
import { PackChatThreadItem } from "@/payroll/pack-chat/pack-chat-thread-item";
import { isTemporaryPackChatError } from "@/payroll/pack-chat/thread-error-copy";
import type { IPackChatThreadSummary } from "@/payroll/pack-chat/types";

/**
 * A technician's own questions about this pack.
 *
 * Stacked and expandable rather than a list beside a reading pane: a technician
 * has one or two questions, not a queue, and a master-detail split spends most of
 * its width telling someone with a single thread to go and select it. The first
 * open thread starts expanded; the rest stay folded until asked for.
 */
export const PackQuestionHistory = ({
  businessId,
  runId,
  threads,
  loading,
  error,
  onRetry,
  sentThreadId,
}: {
  businessId: string | null;
  runId: string;
  threads: IPackChatThreadSummary[];
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  sentThreadId?: string | null;
}) => {
  // Only the threads the reader has folded or unfolded by hand.
  const [expandedOverrides, setExpandedOverrides] = useState<
    Record<string, boolean>
  >({});

  const [selection, setSelection] = useState<{
    threadId: string | null;
    sentThreadId?: string | null;
  }>({ threadId: null });

  if (loading) return <PackChatState layout="collection" loading />;

  const refreshFailed = threads.length > 0 && isTemporaryPackChatError(error);

  if (error && !refreshFailed) {
    return (
      <PackChatState
        error={error}
        title="Questions couldn't be loaded"
        body="Try again. If it keeps failing, contact support."
        layout="collection"
        action={
          onRetry ? (
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={onRetry}
            >
              Try again
            </Button>
          ) : null
        }
      />
    );
  }

  if (threads.length === 0) {
    return (
      <Empty className={emptyStateVariants({ layout: "inline" })}>
        <EmptyHeader>
          <EmptyTitle>No questions yet</EmptyTitle>
          <EmptyDescription>
            Tap the flag beside any figure on <b>My pay</b> to ask about it. It
            shows up here with its replies.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  // Choose one conversation when data arrives, then keep it through server
  // reordering and closure so background updates cannot remove a reply draft.
  const submittedThreadArrived = Boolean(
    sentThreadId &&
      sentThreadId !== selection.sentThreadId &&
      threads.some((thread) => thread.id === sentThreadId),
  );
  const selectedThreadExists = threads.some(
    (thread) => thread.id === selection.threadId,
  );
  const defaultExpandedId = submittedThreadArrived
    ? (sentThreadId ?? null)
    : selectedThreadExists
      ? selection.threadId
      : (threads.find((thread) => thread.status === "open")?.id ?? null);

  if (defaultExpandedId !== selection.threadId || submittedThreadArrived) {
    setSelection({
      threadId: defaultExpandedId,
      sentThreadId: submittedThreadArrived
        ? sentThreadId
        : selection.sentThreadId,
    });
  }
  const defaultExpanded = (thread: IPackChatThreadSummary) =>
    thread.id === defaultExpandedId;

  const openCount = threads.filter((thread) => thread.status === "open").length;

  return (
    <Stack className="w-full" align="start" gap={2}>
      {refreshFailed && <PackChatRefreshError onRetry={onRetry} />}
      {/* No title: the tab this sits under already says "Questions". What is
          worth a line is how they stand. */}
      <Text variant="caption" tone="muted">
        {openCount === 0 ? "All answered" : `${openCount} open`}
      </Text>

      <Stack className="w-full" gap={2} align="stretch">
        {threads.map((thread) => (
          <PackChatThreadItem
            key={thread.id}
            businessId={businessId}
            runId={runId}
            thread={thread}
            scrollToThread={thread.id === sentThreadId}
            expanded={expandedOverrides[thread.id] ?? defaultExpanded(thread)}
            onToggle={() =>
              setExpandedOverrides((current) => ({
                ...current,
                [thread.id]: !(current[thread.id] ?? defaultExpanded(thread)),
              }))
            }
          />
        ))}
      </Stack>
    </Stack>
  );
};
